import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { StatusPill, PriorityBadge } from "../../components/Pills";
import EnergyFlow from "../../components/EnergyFlow";
import { toast } from "sonner";
import { Pause, Play, Square } from "lucide-react";

export default function ConsumerSessions() {
  const [sessions, setSessions] = useState([]);

  const refresh = async () => {
    const { data } = await api.get("/sessions?scope=mine");
    setSessions(data);
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
  }, []);

  const action = async (id, kind) => {
    try {
      await api.post(`/sessions/${id}/${kind}`);
      toast.success(`Session ${kind}d`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const active = sessions.filter((s) => s.status === "active" || s.status === "paused");

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="bin-label">LIVE</div>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Energy Sessions</h1>
        <p className="text-sm text-zinc-500 mt-2">Real-time delivery, priority and cost.</p>
      </div>

      {active.length === 0 && (
        <div className="bin-card p-12 text-center">
          <div className="bin-label mb-3">NO ACTIVE SESSIONS</div>
          <p className="text-sm text-zinc-500">Choose a station to begin routing power to your device.</p>
        </div>
      )}

      <div className="space-y-4">
        {active.map((s) => (
          <div key={s.id} data-testid={`session-${s.id}`} className="bin-card p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="bin-label">SESSION · {s.id.slice(0, 8)}</div>
                <div className="text-xl font-bold mt-1">{s.device_name}</div>
                <div className="text-xs text-zinc-500 font-mono">@ {s.station_name}</div>
              </div>
              <div className="flex items-center gap-2">
                <PriorityBadge priority={s.priority} />
                <StatusPill status={s.status} />
              </div>
            </div>

            <EnergyFlow priority={s.priority} active={s.status === "active"} height={80} />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <Stat label="Delivered" value={`${s.energy_kwh.toFixed(4)} kWh`} live />
              <Stat label="Rate" value={`${s.rate_kw.toFixed(3)} kW`} />
              <Stat label="Cost" value={s.price_per_kwh === 0 ? "FREE" : `$${s.cost_usd.toFixed(4)}`} live />
              <Stat label="Target" value={`${s.target_kwh.toFixed(2)} kWh`} />
            </div>

            <div className="mt-4 flex gap-2">
              {s.status === "active" ? (
                <button
                  data-testid={`session-pause-${s.id}`}
                  onClick={() => action(s.id, "pause")}
                  className="bin-btn inline-flex items-center gap-2"
                >
                  <Pause className="w-3.5 h-3.5" /> Pause
                </button>
              ) : (
                <button
                  data-testid={`session-resume-${s.id}`}
                  onClick={() => action(s.id, "resume")}
                  className="bin-btn inline-flex items-center gap-2"
                >
                  <Play className="w-3.5 h-3.5" /> Resume
                </button>
              )}
              <button
                data-testid={`session-stop-${s.id}`}
                onClick={() => action(s.id, "stop")}
                className="bin-btn bin-btn-danger inline-flex items-center gap-2"
              >
                <Square className="w-3.5 h-3.5" /> End session
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, live }) {
  return (
    <div className="border border-zinc-800 rounded p-3">
      <div className="bin-label">{label}</div>
      <div key={value} className={`text-lg font-mono font-bold mt-1 ${live ? "bin-tick" : ""}`}>{value}</div>
    </div>
  );
}
